<template>
  <article
    class="bedmatpick"
    :class="[features.backgroundColor]"
    :data-sku="BEDMAT_SKU"
    data-produktgruppe="BSW"
  >
    <figure v-if="features.callout?.istAktiv">
      <p>
        <span>Zusätzlich beantragen:</span>
        <b>Extra Schutz für 0 €</b>
      </p>
    </figure>
    <div class="content">
      <header>
        <strong>caretex® Eco Design</strong>
        <h3>Waschbare Bettschutzeinlagen</h3>
        <p v-if="features.subline?.istAktiv" class="text-s">
          {{ features.subline.text }}
        </p>
      </header>
      <ul v-if="features.perks?.istAktiv" v-html="features.perks.message"></ul>
      <aside
        v-if="features.status?.name && features.status?.iconName && features.status?.iconColor"
      >
        <PflegeIcon :name="features.status.iconName" :color="features.status.iconColor" />
        <p>{{ features.status.text }}</p>
      </aside>
      <picture>
        <source
          :srcset="`${imgUrl}/m/150x150/filters:format(webp):quality(90), ${imgUrl}/m/300x300/filters:format(webp) 810w, ${imgUrl}/m/430x430/filters:format(webp) 2x`"
          type="image/webp"
        />
        <img
          alt="Waschbare Bettschutzeinlage"
          loading="lazy"
          title="So sehen die waschbaren Bettschutzeinlagen zum mehrfachen Verwenden aus"
          data-copyright="Pflege.de"
          :src="`${imgUrl}/m/430x430/filters:format(webp)`"
        />
      </picture>
      <div>
        <nav>
          <button @click="showDescriptionModal = true">
            Produktdetails
            <PflegeIcon name="info-line" />
          </button>
          <button @click="showManualModal = true">
            Anwendungshinweise
            <PflegeIcon name="info-line" />
          </button>
        </nav>
        <fieldset v-if="features.quantitybutton?.istAktiv">
          <PflegeQuantitybutton
            v-model.number="gewaehlteAnzahl"
            :max="features.quantitybutton.max"
            :min="anzahlBereitsGelieferte"
            :has-error="hasGewaehlteAnzahlError"
            :error-message="features.quantitybutton.errorMessage"
            readonly
          />
        </fieldset>
      </div>
      <PflegeAlert
        v-if="features.alert?.istAktiv"
        :heading="features.alert.heading"
        variant="warning"
      >
        <div v-if="features.alert?.message" v-html="features.alert.message"></div>
      </PflegeAlert>
      <footer>
        <PflegeButton
          v-if="showNavigation && features.submit?.istAktiv"
          :label="features.submit.text"
          :loading="isSendDecisionPending"
          @click="submit"
        />
      </footer>
    </div>

    <PflegeModal v-model="showDescriptionModal" class="details" heading="Produktdetails">
      <ul>
        <li>Vierlagige, waschbare Bettschutzeinlagen aus weichem Polyester</li>
        <li>Saugen Flüssigkeit schnell auf und speichern sie zuverlässig</li>
        <li>Bieten Pflegebedürftigen hohen Liegekomfort und Trockenheit</li>
        <li>Schützen Matratzen, Sitzmöbel und mehr vor Körperflüssigkeiten</li>
        <li>Ideal geeignet bei Inkontinenz</li>
        <li>75 cm x 85 cm</li>
        <li>Kochfest (95 Grad Celsius), bedingt trocknergeeignet</li>
      </ul>
    </PflegeModal>
    <PflegeModal v-model="showManualModal" class="hints" heading="Anwendungshinweise">
      <ol>
        <li>Bettschutzeinlage mit blauer Seite nach unten auf Sitz- oder Liegefläche legen</li>
        <li>Auf faltenfreie Platzierung achten</li>
        <li>Nass gewordene Bettschutzeinlage wechseln und heiß waschen</li>
      </ol>
    </PflegeModal>
  </article>
</template>

<script setup>
import { ref, watch, computed } from 'vue'
import { decodeJwt } from 'jose'
import { CUSTOMER_STATUS, BEDMAT_SKU } from '../../utils/constants'
import { useDebounce, useDecisionApi } from '../../composables'

const props = defineProps({
  authTokenContext: {
    type: String,
    required: false,
    default: ''
  },
  decisionContexts: {
    type: Array,
    required: false,
    default: () => []
  },
  customerStatus: {
    type: String,
    required: false,
    default: CUSTOMER_STATUS.BESTANDSKUNDE,
    validator(value) {
      return Object.values(CUSTOMER_STATUS).includes(value)
    }
  },
  showNavigation: {
    type: Boolean,
    required: false,
    default: true
  },
  initalClientDataJwt: {
    type: String,
    required: false,
    default: ''
  }
})

const emit = defineEmits({
  error: (errorReason) => {
    return ['authTokenExpired', 'apiError', 'apiNotAvailable'].includes(errorReason)
  },
  submit: (submitObject) => {
    return submitObject.reason
  }
})

const imgUrl =
  'https://assets.pflege.de/f/167189/2160x810/d06befe1c3/240403_packshots-waschbbettschutzeinlage.jpg'
const gewaehlteAnzahl = ref(0)
const showManualModal = ref(false)
const showDescriptionModal = ref(false)
const decisionJwt = ref('')
const anzahlBereitsGelieferte = ref(0)
const features = ref({})
const { sendDecision, isSendDecisionPending } = useDecisionApi()
const decisionCall = async (isInitCall = false) => {
  if (isInitCall && props.initalClientDataJwt) {
    const claims = decodeJwt(props.initalClientDataJwt)
    gewaehlteAnzahl.value = claims.data?.produkte?.[BEDMAT_SKU]?.gewaehlteAnzahl ?? 0
  }
  return await sendDecision(
    {
      decision: `bedmatstatus${props.customerStatus === CUSTOMER_STATUS.NEUKUNDE ? '-new' : ''}.v1`,
      contexts: props.decisionContexts.concat([props.authTokenContext]),
      client: {
        produkte: { [BEDMAT_SKU]: { gewaehlteAnzahl: gewaehlteAnzahl.value } }
      }
    },
    !isInitCall
  )
    .then((response) => {
      if (!response.ok) {
        console.error(`decisionAPI server-error`, response)
        emit('error', 'apiError')
        return
      }
      return response.json()
    })
    .then((body) => {
      if (!body) {
        return
      }
      decisionJwt.value = body.jwt
      anzahlBereitsGelieferte.value = body.result.anzahlBereitsGelieferte || 0
      features.value = body.result.features
      if (isInitCall && !props.initalClientDataJwt) {
        gewaehlteAnzahl.value = parseInt(anzahlBereitsGelieferte.value)
      }
      return body
    })
    .catch((e) => {
      if (e.name === 'AbortError') {
        return
      }
      console.error('decisionAPI not available', e)
      emit('error', 'apiNotAvailable')
    })
}

const debouncedDecisionCall = useDebounce(decisionCall)
watch(
  () => props.decisionContexts,
  () => debouncedDecisionCall(true)
)
watch(
  () => props.authTokenContext,
  () => debouncedDecisionCall()
)
watch(gewaehlteAnzahl, () => debouncedDecisionCall())

const hasGewaehlteAnzahlError = ref(false)
const submit = () => {
  if (
    features.value?.quantitybutton?.istAktiv &&
    (gewaehlteAnzahl.value <= 0 ||
      gewaehlteAnzahl.value == anzahlBereitsGelieferte.value ||
      gewaehlteAnzahl.value > features.value?.quantitybutton?.max) &&
    props.customerStatus === CUSTOMER_STATUS.BESTANDSKUNDE
  ) {
    hasGewaehlteAnzahlError.value = true
    return
  }
  hasGewaehlteAnzahlError.value = false
  emit('submit', {
    gewaehlteAnzahl: gewaehlteAnzahl,
    reason: features.value?.submit?.action,
    jwt: decisionJwt
  })
}

/* Exposes */
defineExpose({
  submit,
  disableSubmitButton: computed(
    () => !features.value?.submit?.istAktiv || isSendDecisionPending.value
  ),
  decisionJwt,
  isLoading: isSendDecisionPending
})

await decisionCall(true)
</script>

<style>
.bedmatpick {
  container-type: inline-size;
  display: flex;
  gap: 1em;
  flex-direction: column;
  padding: 1em;
  border-radius: 0.5em;
  box-shadow: 0 0 0.75em 0px #00000040;
}

.bedmatpick nav button {
  appearance: none;
  background: none;
  border: 0;
  text-align: left;
  padding: 0;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 0.25em;
  cursor: pointer;
}
.bedmatpick nav button .icon {
  --size: 18px;
}

.bedmatpick > figure {
  margin: 0;
  padding-bottom: 1em;

  @media (prefers-reduced-motion: no-preference) {
    animation: float 3s ease-in-out infinite alternate;
  }
}
.bedmatpick > figure > p {
  --borderRadius: 0.33em;
  background-color: var(--blue);
  border-radius: var(--borderRadius);
  padding: 1em 1em 0.5rem 1em;
  color: var(--white);
  display: flex;
  justify-content: center;
  margin: 0 auto;
  position: relative;
  flex-wrap: wrap;
  gap: 0.25em;
  line-height: 1em;
  text-align: center;
}
.bedmatpick > figure > p::after {
  content: '';
  display: block;
  position: absolute;
  left: var(--borderRadius);
  bottom: calc(-1em + 1px);
  height: 1em;
  width: calc(100% - var(--borderRadius) * 2);
  background-color: var(--blue);
  clip-path: polygon(50% 100%, 0 0, 100% 0);
}

.bedmatpick > .content {
  --maxImageSize: 9em;
  display: grid;
  grid-template-columns: clamp(var(--maxImageSize), calc(25cqw - 1em), 15em) 1fr;
  gap: 1em;
}
.bedmatpick > .content > * {
  grid-column-end: span 2;
}
.bedmatpick > .content > header {
  display: flex;
  flex-direction: column;
  gap: 0.25em;
}
.bedmatpick > .content > header h3 {
  font-size: 1.75rem;
  line-height: 1.25;
}
@container (width > 430px) {
  .bedmatpick > .content {
    gap: 1em;
  }
  .bedmatpick > .content > * {
    grid-column-start: 2;
    grid-column-end: span 1;
  }
  .bedmatpick > .content > header .h5 {
    font-size: 1.75em;
  }
  .bedmatpick > .content > header strong {
    font-size: 1.33em;
  }
}

.bedmatpick > .content > ul {
  margin: 0;
  padding-inline-start: 1.5em;
}

.bedmatpick > .content > :is(picture, div) {
  grid-column-end: span 1;
}
@container (width > 430px) {
  .bedmatpick > .content > picture {
    grid-column-start: 1;
    grid-row: 1 / span 5;
  }
}
.bedmatpick > .content > picture img {
  background-color: var(--mint-light);
  width: max(var(--maxImageSize), 100%);
  height: auto;
  border-radius: 0.5em;
  position: sticky;
  top: 0.5em;
}
.bedmatpick > .content > div {
  display: flex;
  flex-direction: column;
  gap: 1em;
}
.bedmatpick > .content > div > nav {
  display: flex;
  flex-direction: column;
  gap: 0.5em;
}
.bedmatpick > .content > div > fieldset {
  padding: 0;
  border: none;
}
.bedmatpick > .content > .alert {
  border-radius: 0.5em;
  border: 1px solid #00000040;
  background-color: #fff8;
}
.bedmatpick > .content > .alert .h6 {
  font-size: 18px;
  color: var(--black);
}
.bedmatpick > .content > .alert > div > div {
  padding-block-start: 1em;
}

.bedmatpick > .content > aside {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 0.5em;
}
.bedmatpick > .content > aside > *:not(svg) {
  grid-column-start: 2;
}
.bedmatpick > .content > aside > svg {
  --size: 1.5rem;
}

.bedmatpick > .content > footer {
  padding-block-start: 0.5em;
}
.bedmatpick > .content > footer:empty {
  display: none;
}

@keyframes float {
  0% {
    translate: 0 -0.15em;
  }
  100% {
    translate: 0 0.15em;
  }
}
</style>
